<template>
    <div class="playlist-library overflow-y-auto h-screen-header" ref="playlistview">
        <section class="playlist-hero">
            <div class="playlist-hero__copy">
                <div class="playlist-hero__label">{{ $tt('播放列表') }}</div>
                <h1>{{ $tt('把想听的歌组织成清楚的队列') }}</h1>
                <p>{{ $tt('导入外部歌单、创建智能规则，或直接进入已有列表继续整理和播放。') }}</p>
                <div class="playlist-hero__actions">
                    <button type="button" class="btn btn-primary btn-sm" @click="handleImportPlayList">
                        {{ $tt('导入歌单') }}
                    </button>
                    <button type="button" class="btn btn-outline btn-sm" @click="handleAiPlayList">
                        {{ $tt('智能歌单') }}
                    </button>
                    <button type="button" class="btn btn-ghost btn-sm" :disabled="loading" @click="refreshPlaylists">
                        {{ $tt('刷新') }}
                    </button>
                </div>
            </div>

            <div class="playlist-hero__stage" aria-hidden="true">
                <div class="playlist-deck">
                    <div
                        v-for="(item, index) in heroPlaylists"
                        :key="`${item.id || index}_${index}`"
                        class="playlist-deck__item"
                        :style="{ '--playlist-index': index }"
                    >
                        <img :src="item.image || '/static/dist/img/album_null.jpg'" alt="">
                        <div>
                            <strong>{{ item.name || $tt('未命名播放列表') }}</strong>
                            <span>{{ Number(item.trackCount || 0) }} {{ $tt('歌曲') }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="playlist-toolbar">
            <div class="playlist-toolbar__tabs scrollbar-hide" role="tablist" :aria-label="$tt('播放列表排序')">
                <button
                    v-for="{ value, text } in options"
                    :key="value"
                    type="button"
                    :class="['playlist-tab', sortValue === value ? 'is-active' : '']"
                    role="tab"
                    :aria-selected="sortValue === value ? 'true' : 'false'"
                    @click="switchSort(value)"
                >
                    {{ text }}
                </button>
            </div>
            <div class="playlist-toolbar__actions">
                <button type="button" class="playlist-action-button" @click="handleImportPlayList">
                    {{ $tt('导入') }}
                </button>
                <button type="button" class="playlist-action-button is-primary" @click="handleAiPlayList">
                    {{ $tt('智能歌单') }}
                </button>
            </div>
        </section>

        <section class="playlist-status-strip">
            <div>
                <span class="playlist-status-strip__label">{{ $tt('当前规则') }}</span>
                <strong>{{ currentSortLabel }}</strong>
            </div>
            <div>
                <span class="playlist-status-strip__label">{{ $tt('已载入') }}</span>
                <strong>{{ playlist.length }}</strong>
            </div>
            <div>
                <span class="playlist-status-strip__label">{{ $tt('歌曲总数') }}</span>
                <strong>{{ totalTrackCount }}</strong>
            </div>
            <div>
                <span class="playlist-status-strip__label">{{ $tt('智能歌单') }}</span>
                <strong>{{ dynamicPlaylistCount }}</strong>
            </div>
        </section>

        <section class="playlist-content">
            <Tiles v-if="playlist.length > 0">
                <div
                    v-for="(item, index) in playlist"
                    :key="item.id"
                    :class="index < 18 ? 'playlist-tile-shell is-animated' : 'playlist-tile-shell'"
                    :style="index < 18 ? { '--tile-index': index } : {}"
                >
                    <Tile
                        :image="item.image"
                        :to="{ name: 'playlist', params: { id: item.id } }"
                        :title="item.name"
                        :square="false"
                        :small="false"
                        :allow-h-scroll="false"
                    >
                        <template #text>
                            <router-link class="playlist-card-meta" :to="{ name: 'playlist', params: { id: item.id } }">
                                <span><strong>{{ Number(item.trackCount || 0) }}</strong> {{ $tt('歌曲') }}</span>
                                <span v-if="item.isDynamic">{{ $tt('动态更新') }}</span>
                                <span v-else-if="item.owner">{{ item.owner }}</span>
                            </router-link>
                        </template>
                        <template #play>
                            <button type="button" class="playlist-play-button" :title="$tt('播放播放列表')" @click.stop="playNow(item.id)">
                                <svg class="fill-current w-12 h-12" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M512 1024A512 512 0 1 1 512 0a512 512 0 0 1 0 1024zM383.232 287.616v448l384-223.104-384-224.896z"></path>
                                </svg>
                            </button>
                        </template>
                    </Tile>
                </div>
            </Tiles>

            <div v-if="loading" class="playlist-loading">
                <div class="playlist-loading__grid">
                    <span v-for="index in 8" :key="index"></span>
                </div>
                <p>{{ $tt('正在载入播放列表') }}</p>
            </div>

            <div v-if="!loading && playlist.length === 0" class="playlist-empty">
                <div class="playlist-empty__art">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <h3>{{ $tt('暂无播放列表') }}</h3>
                <p>{{ $tt('可以导入 m3u8 文件，或用智能规则创建一个播放列表。') }}</p>
                <div class="playlist-empty__actions">
                    <button type="button" class="btn btn-primary btn-sm" @click="handleImportPlayList">{{ $tt('导入歌单') }}</button>
                    <button type="button" class="btn btn-outline btn-sm" @click="handleAiPlayList">{{ $tt('智能歌单') }}</button>
                </div>
            </div>
        </section>

        <button v-if="curScrollTop > 700" @click="scrollToTop" class="playlist-scroll-top" :title="$tt('返回顶部')">
            <svg class="icon h-5 w-5 fill-current" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path d="M890.5 755.3L537.9 269.2c-12.8-17.6-39-17.6-51.7 0L133.5 755.3c-3.8 5.3-0.1 12.7 6.5 12.7h75c5.1 0 9.9-2.5 12.9-6.6L512 369.8l284.1 391.6c3 4.1 7.8 6.6 12.9 6.6h75c6.5 0 10.3-7.4 6.5-12.7z"></path>
            </svg>
        </button>

        <dialog id="ai-list" ref="ai-list" class="modal playlist-modal playlist-modal--smart">
            <div class="modal-box playlist-modal__box playlist-modal__box--smart">
                <div class="playlist-modal__head">
                    <div>
                        <div class="playlist-modal__eyebrow">{{ $tt('智能歌单') }}</div>
                        <h3 class="playlist-modal__title">{{ $tt('创建智能播放列表') }}</h3>
                    </div>
                    <form method="dialog">
                        <button class="playlist-modal__icon-btn" :aria-label="$tt('关闭')">x</button>
                    </form>
                </div>
                <div class="playlist-modal__body playlist-modal__body--smart">
                    <div class="playlist-modal__field">
                        <label class="playlist-modal__label">{{ $tt('列表名称') }}</label>
                        <input
                            type="text"
                            :placeholder="$tt('请输入播放列表名称')"
                            class="input input-bordered input-sm playlist-modal__input"
                            v-model="playlistName"
                        />
                    </div>
                    <div class="playlist-modal__rules">
                        <div class="playlist-modal__rule-group" v-for="(item, index) in conditions" :key="'a' + index">
                            <div class="playlist-modal__rule-head">
                                <div v-if="index === 0" class="playlist-modal__rule-title">{{ $tt('满足条件') }}</div>
                                <div v-else>
                                    <div class="playlist-modal__logic-chip">{{ $tt('或') }}</div>
                                    <span class="playlist-modal__rule-title">{{ $tt('满足条件') }}</span>
                                </div>
                                <div class="playlist-modal__rule-actions">
                                    <button type="button" class="btn btn-square btn-outline btn-sm" @click="addItem">+</button>
                                    <button type="button" class="btn btn-square btn-outline btn-sm" @click="removeItem(index)" v-if="conditions.length > 1">-</button>
                                </div>
                            </div>
                            <div
                                class="playlist-modal__condition-row"
                                v-for="(sub, subIndex) in item"
                                :key="'s' + index + 'a' + subIndex"
                            >
                                <div v-if="subIndex >= 1" class="playlist-modal__logic-chip playlist-modal__logic-chip--and">{{ $tt('且') }}</div>
                                <single-select v-model="sub.key" :options="keyOptions" class="playlist-modal__select" />
                                <single-select
                                    v-if="boolKeyList.indexOf(sub.key) !== -1"
                                    v-model="sub.operate"
                                    :options="operateOptions"
                                    class="playlist-modal__select playlist-modal__select--operator"
                                />
                                <single-select
                                    v-else-if="numberKeyList.indexOf(sub.key) !== -1"
                                    v-model="sub.operate"
                                    :options="operateGteOptions"
                                    class="playlist-modal__select playlist-modal__select--operator"
                                />
                                <single-select
                                    v-else
                                    v-model="sub.operate"
                                    :options="valueOptions"
                                    class="playlist-modal__select playlist-modal__select--operator"
                                />
                                <select
                                    class="select select-bordered select-sm playlist-modal__input"
                                    v-if="boolKeyList.indexOf(sub.key) !== -1"
                                    v-model="sub.value"
                                >
                                    <option value="">True</option>
                                </select>
                                <div class="playlist-modal__value-field" v-else-if="numberKeyList.indexOf(sub.key) !== -1">
                                    <input
                                        type="number"
                                        :placeholder="$tt('请输入')"
                                        class="input input-bordered input-sm playlist-modal__input"
                                        v-model="sub.value"
                                    />
                                    <div class="playlist-modal__unit" v-if="sub.key === 'bit-rate'">kps</div>
                                    <div class="playlist-modal__unit" v-else-if="sub.key === 'size'">MB</div>
                                    <div class="playlist-modal__unit" v-else-if="['play-count', 'album-play-count', 'artist-play-count'].includes(sub.key)">{{ $tt('次') }}</div>
                                    <div class="playlist-modal__unit" v-else-if="sub.key === 'year'">{{ $tt('年') }}</div>
                                    <div class="playlist-modal__unit" v-else-if="sub.key === 'duration'">{{ $tt('秒') }}</div>
                                    <div class="playlist-modal__unit" v-else-if="sub.key === 'accessed-date'">{{ $tt('天') }}</div>
                                    <div class="playlist-modal__unit" v-else-if="sub.key === 'created-at'">{{ $tt('天') }}</div>
                                    <div class="playlist-modal__unit" v-else-if="sub.key === 'cover-width'">px</div>
                                    <div class="playlist-modal__unit" v-else-if="sub.key === 'cover-height'">px</div>
                                    <div v-else></div>
                                </div>
                                <div class="playlist-modal__value-field" v-else>
                                    <input
                                        type="text"
                                        :placeholder="$tt('请输入')"
                                        class="input input-bordered input-sm playlist-modal__input"
                                        v-model="sub.value"
                                    />
                                </div>
                                <div class="playlist-modal__rule-actions">
                                    <button type="button" class="btn btn-square btn-outline btn-sm" @click="addSubItem(index)">+</button>
                                    <button
                                        type="button"
                                        class="btn btn-square btn-outline btn-sm"
                                        @click="removeSubItem(index, subIndex)"
                                        v-if="item.length > 1"
                                    >-</button>
                                </div>
                            </div>
                            <div class="playlist-modal__divider" v-if="conditions.length > 1 && conditions.length !== index + 1">{{ $tt('或') }}</div>
                        </div>
                    </div>
                    <div class="playlist-modal__limit-row">
                        <div class="playlist-modal__label">{{ $tt('最多') }}</div>
                        <input
                            type="number"
                            min="1"
                            :placeholder="$tt('请输入')"
                            class="input input-bordered input-sm playlist-modal__number"
                            v-model.number="limit"
                        />
                        <select class="select select-bordered select-sm playlist-modal__unit-select" v-model="unit">
                            <option value="item">{{ $tt('项') }}</option>
                        </select>
                        <div class="playlist-modal__label">{{ $tt('选择标准') }}</div>
                        <single-select v-model="selectStandard" :options="selectStandardOptions" />
                    </div>
                    <div class="playlist-modal__toggle">
                        <label class="cursor-pointer label">
                            <input type="checkbox" class="checkbox checkbox-secondary" v-model="isDynamic" />
                            <span class="label-text">{{ $tt('动态更新') }}</span>
                        </label>
                    </div>
                </div>
                <div class="modal-action playlist-modal__actionbar">
                    <form method="dialog">
                        <button type="button" class="btn btn-neutral btn-sm mr-2" @click="handleCreate">{{ $tt('确认') }}</button>
                        <button class="btn btn-sm">{{ $tt('取消') }}</button>
                    </form>
                </div>
            </div>
        </dialog>

        <dialog ref="import-list" class="modal playlist-modal playlist-modal--import">
            <div class="modal-box playlist-modal__box playlist-modal__box--import">
                <form method="dialog">
                    <button
                        class="playlist-modal__icon-btn playlist-modal__icon-btn--floating"
                        :disabled="isImportingPlaylist"
                        :aria-label="$tt('关闭')"
                    >x</button>
                </form>
                <div class="playlist-modal__eyebrow">{{ $tt('文件导入') }}</div>
                <h3 class="playlist-modal__title">{{ $tt('导入播放列表') }}</h3>
                <div class="playlist-modal__hint">
                    {{ $tt('导入一个播放列表，支持 m3u8 文件。') }}
                </div>
                <input
                    ref="playlist-file-input"
                    type="file"
                    class="file-input file-input-bordered file-input-sm playlist-modal__file"
                    :disabled="isImportingPlaylist"
                    @change="onFileChange"
                />
                <div class="form-control w-full playlist-modal__toggle playlist-modal__toggle--loose">
                    <label class="cursor-pointer label">
                        <span class="label-text">{{ $tt('替换歌曲路径前缀') }}</span>
                        <input type="checkbox" class="checkbox checkbox-secondary" v-model="replaceTrackPath" :disabled="isImportingPlaylist" />
                    </label>
                </div>
                <div v-if="replaceTrackPath" class="form-control w-full playlist-modal__field">
                    <label class="label">
                        <span class="label-text">{{ $tt('原始路径前缀 (要被替换的部分)') }}</span>
                    </label>
                    <input
                        type="text"
                        :placeholder="$tt('例如: /old/music/path/ 或 C:\\Music\\')"
                        class="input input-bordered input-sm playlist-modal__input"
                        :disabled="isImportingPlaylist"
                        v-model="originalPathPrefix"
                    />
                    <label class="label">
                        <span class="label-text-alt playlist-modal__help">{{ $tt('将替换为系统音乐库路径，保留相对路径结构') }}</span>
                    </label>
                </div>
                <div class="modal-action playlist-modal__actionbar">
                    <button class="btn btn-sm btn-neutral" :disabled="isImportingPlaylist || !selectedFile" @click="uploadFile">
                        <span v-if="isImportingPlaylist" class="loading loading-spinner loading-xs"></span>
                        {{ isImportingPlaylist ? $tt('导入中...') : $tt('保存') }}
                    </button>
                    <form method="dialog">
                        <button class="btn btn-sm" :disabled="isImportingPlaylist">{{ $tt('取消') }}</button>
                    </form>
                </div>
            </div>
        </dialog>
    </div>
</template>

<script>
import Tiles from '@/components/library/Tiles'
import Tile from '@/components/library/Tile'
import SingleSelect from '@/components/iview/SingleSelect'

export default {
    name: 'playlists',
    components: {
        Tiles,
        Tile,
        SingleSelect
    },
    data() {
        return {
            loading: true,
            curScrollTop: 0,
            isScrollBound: false,
            options: [
                { text: this.$tt('最近添加'), value: 'recently-added' },
                { text: this.$tt('A-Z排序'), value: 'a-z' }
            ],
            playlist: [],
            playlistName: '',
            limit: 25,
            unit: 'item',
            selectStandard: '?',
            isDynamic: false,
            showAddModal: false,
            conditions: [[
                { 'key': 'favourite-track', 'operate': 'is', 'value': '' }
            ]],
            boolKeyList: ['favourite-track', 'favourite-album', 'favourite-artist', 'album-img', 'artist-img'],
            numberKeyList: ['play-count', 'album-play-count', 'artist-play-count', 'sample-rate', 'bit-rate', 'size', 'year', 'duration', 'accessed-date',
                'created-at', 'rating', 'cover-width', 'cover-height'],
            selectedFile: null,
            replaceTrackPath: false,
            originalPathPrefix: '',
            isImportingPlaylist: false,
            keyOptions: [
                { id: 'favourite-track', name: this.$tt('标记为喜爱的歌曲') },
                { id: 'favourite-album', name: this.$tt('标记为喜爱的专辑') },
                { id: 'favourite-artist', name: this.$tt('标记为喜爱的艺术家') },
                { id: 'rating', name: this.$tt('评分') },
                { id: 'file-path', name: this.$tt('文件路径') },
                { id: 'title', name: this.$tt('标题') },
                { id: 'album', name: this.$tt('专辑') },
                { id: 'artist', name: this.$tt('艺术家') },
                { id: 'genre', name: this.$tt('流派') },
                { id: 'comment', name: this.$tt('描述') },
                { id: 'lyrics', name: this.$tt('歌词') },
                { id: 'language', name: this.$tt('语言') },
                { id: 'label', name: this.$tt('标签') },
                { id: 'play-count', name: this.$tt('播放次数') },
                { id: 'album-play-count', name: this.$tt('专辑播放次数') },
                { id: 'artist-play-count', name: this.$tt('艺术家播放次数') },
                { id: 'bit-rate', name: this.$tt('比特率') },
                { id: 'size', name: this.$tt('大小') },
                { id: 'year', name: this.$tt('年份') },
                { id: 'duration', name: this.$tt('时长') },
                { id: 'accessed-date', name: this.$tt('距离最近播放') },
                { id: 'created-at', name: this.$tt('距离最近添加') },
                { id: 'album-img', name: this.$tt('专辑图片') },
                { id: 'artist-img', name: this.$tt('艺术家图片') },
                { id: 'cover-width', name: this.$tt('封面宽度') },
                { id: 'cover-height', name: this.$tt('封面高度') },
            ],
            operateOptions: [
                { id: 'is', name: this.$tt('是') },
                { id: 'not', name: this.$tt('不是') },
            ],
            operateGteOptions: [
                { id: 'gte', name: this.$tt('大于') },
                { id: 'lte', name: this.$tt('小于') },
                { id: 'is', name: this.$tt('是') },
                { id: 'not', name: this.$tt('不是') },
            ],
            valueOptions: [
                { id: 'contain', name: this.$tt('包含') },
                { id: 'not_contain', name: this.$tt('不包含') },
                { id: 'startwith', name: this.$tt('开头是') },
                { id: 'endwith', name: this.$tt('结尾是') },
                { id: 'is', name: this.$tt('是') },
                { id: 'not', name: this.$tt('不是') },
            ],
            selectStandardOptions: [
                { id: '?', name: this.$tt('随机') },
                { id: '-accessed_date', name: this.$tt('最近播放') },
                { id: 'accessed_date', name: this.$tt('最早播放') },
                { id: '-plays_count', name: this.$tt('最常播放') },
                { id: 'plays_count', name: this.$tt('最少播放') },
                { id: '-created_at', name: this.$tt('最近添加') },
                { id: 'created_at', name: this.$tt('最早添加') },
            ]
        }
    },
    computed: {
        sortValue() {
            return this.$route.params.sort || 'recently-added'
        },
        currentSortLabel() {
            const option = this.options.find(item => item.value === this.sortValue)
            return option ? option.text : this.$tt('最近添加')
        },
        totalTrackCount() {
            return this.playlist.reduce((sum, item) => sum + Number(item.trackCount || 0), 0)
        },
        dynamicPlaylistCount() {
            return this.playlist.filter(item => item.isDynamic).length
        },
        heroPlaylists() {
            const items = this.playlist.slice(0, 4)
            while (items.length < 4) {
                items.push({})
            }
            return items
        }
    },
    watch: {
        sortValue(newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                this.refreshPlaylists()
            }
        }
    },
    created() {
        this.refreshPlaylists()
    },
    mounted() {
        this.bindScrollListener()
        this.restoreScroll()
    },
    activated() {
        this.bindScrollListener()
        this.restoreScroll()
    },
    deactivated() {
        this.unbindScrollListener()
    },
    beforeDestroy() {
        this.unbindScrollListener()
    },
    methods: {
        bindScrollListener() {
            if (this.$refs.playlistview && !this.isScrollBound) {
                this.$refs.playlistview.addEventListener('scroll', this.listenScroll)
                window.playlistview = this.$refs.playlistview
                this.isScrollBound = true
            }
        },
        unbindScrollListener() {
            if (this.$refs.playlistview && this.isScrollBound) {
                this.$refs.playlistview.removeEventListener('scroll', this.listenScroll, false)
                this.isScrollBound = false
            }
        },
        restoreScroll() {
            if (this.$refs.playlistview) {
                this.$refs.playlistview.scrollTop = this.curScrollTop || 0
            }
        },
        listenScroll() {
            const container = this.$refs.playlistview || window.playlistview
            this.curScrollTop = container ? container.scrollTop : document.body.scrollTop
        },
        scrollToTop() {
            if (this.$refs.playlistview) {
                this.$refs.playlistview.scrollTop = 0
            }
        },
        switchSort(value) {
            if (this.sortValue === value) {
                this.refreshPlaylists()
                return
            }
            this.$router.push({ path: '/playlists/' + value })
        },
        async refreshPlaylists() {
            this.loading = true
            try {
                this.playlist = await this.$api.subsonic.getPlaylists(this.sortValue)
            } catch (err) {
                this.playlist = []
                if (this.$myMsg && err && err.message !== 'Wrong username or password.') {
                    this.$myMsg.notify({
                        title: this.$tt('载入失败'),
                        content: err.message || this.$tt('播放列表载入失败'),
                        type: 'error',
                        time: 5000,
                    })
                }
            } finally {
                this.loading = false
            }
        },
        resetImportForm() {
            this.selectedFile = null
            this.replaceTrackPath = false
            this.originalPathPrefix = ''
            if (this.$refs['playlist-file-input']) {
                this.$refs['playlist-file-input'].value = ''
            }
        },
        async playNow(id) {
            const playlist = await this.$api.subsonic.getPlaylist(id)
            return this.$store.dispatch('playTrackList', {
                tracks: playlist.tracks
            })
        },
        handleAiPlayList() {
            this.$refs["ai-list"].showModal()
        },
        handleImportPlayList() {
            this.resetImportForm()
            this.$refs["import-list"].showModal()
        },
        addSubItem(index) {
            this.conditions[index].push({ 'key': '', 'operate': '', 'value': '' })
        },
        removeSubItem(index, subIndex) {
            if (this.conditions[index].length > 1) {
                this.conditions[index].splice(subIndex, 1)
            }
        },
        addItem() {
            this.conditions.push([{ 'key': '', 'operate': '', 'value': '' }])
        },
        removeItem(index) {
            this.conditions.splice(index, 1)
        },
        handleCreate() {
            if (!this.playlistName) {
                this.$myMsg.notify({
                    title: this.$tt('警告！'),
                    content: this.$tt('播放列表名称不能为空！'),
                    type: 'warning',
                    time: 2000,
                })
                return
            }
            this.$api.subsonic.createPlaylistAi(this.playlistName,
                JSON.stringify(this.conditions), this.limit, this.selectStandard, this.isDynamic).then(() => {
                    this.$myMsg.notify({
                        title: this.$tt('添加成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.$refs["ai-list"].close()
                    this.refreshPlaylists()
                })
        },
        onFileChange(event) {
            this.selectedFile = event.target.files[0] || null
        },
        async uploadFile() {
            if (this.isImportingPlaylist) {
                return
            }
            if (!this.selectedFile) {
                this.$myMsg.notify({
                    title: this.$tt('警告！'),
                    content: this.$tt('请先选择歌单文件！'),
                    type: 'warning',
                    time: 2000,
                })
                return
            }

            const formData = new FormData()
            formData.append('file', this.selectedFile)
            formData.append('replace_path', this.replaceTrackPath)
            if (this.replaceTrackPath && this.originalPathPrefix) {
                formData.append('original_path_prefix', this.originalPathPrefix)
            }

            this.isImportingPlaylist = true
            try {
                const res = await this.$api.Task.importPlaylist(formData)
                if (res.result) {
                    const data = res.data || {}
                    const statistics = data.statistics || {
                        found_tracks: 0,
                        total_tracks: 0,
                        success_rate: 0
                    }
                    const warnings = data.warnings || null
                    let title = this.$tt('导入成功！')
                    let content = this.$tt('成功导入 {found}/{total} 首歌曲 (成功率: {rate}%)', {
                        found: statistics.found_tracks,
                        total: statistics.total_tracks,
                        rate: statistics.success_rate
                    })
                    let type = 'success'

                    if (data.path_replacement && data.path_replacement.enabled) {
                        const replacement = data.path_replacement
                        content += '\n' + this.$tt('路径替换: {count} 个文件', {
                            count: replacement.replaced_count
                        })
                        content += '\n' + this.$tt('替换规则: {from} -> {to}', {
                            from: replacement.original_prefix,
                            to: replacement.target_prefix
                        })

                        if (warnings && warnings.total_missing > 0) {
                            type = 'warning'
                            title = this.$tt('导入完成但有警告！')
                            content += '\n' + this.$tt('警告: {count} 个文件在替换路径下未找到', {
                                count: warnings.total_missing
                            })

                            if (warnings.missing_files.length > 0) {
                                const missingList = warnings.missing_files
                                    .slice(0, 3)
                                    .map(file => file.relative_path || file.filename)
                                    .join(', ')
                                content += '\n' + this.$tt('缺失文件示例: {files}', { files: missingList })
                                if (warnings.total_missing > 3) {
                                    content += ' ' + this.$tt('等 {count} 个文件', {
                                        count: warnings.total_missing
                                    })
                                }
                            }
                        }
                    }

                    this.$myMsg.notify({
                        title: title,
                        content: content,
                        type: type,
                        time: 4000,
                    })
                    this.resetImportForm()
                    this.$refs["import-list"].close()
                    await this.refreshPlaylists()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('导入失败！'),
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    })
                }
            } catch (error) {
                this.$myMsg.notify({
                    title: this.$tt('导入失败！'),
                    content: error?.message || this.$tt('导入请求失败，请稍后重试'),
                    type: 'error',
                    time: 2000,
                })
            } finally {
                this.isImportingPlaylist = false
            }
        }
    }
}
</script>

<style scoped>
.playlist-library {
    --playlist-radius: 18px;
    --playlist-border: oklch(var(--bc) / 0.12);
    --playlist-muted: oklch(var(--bc) / 0.62);
    --playlist-soft: color-mix(in oklch, oklch(var(--b2)) 72%, transparent);
    min-height: 0;
    overflow-x: hidden;
    border-radius: 1rem;
    color: oklch(var(--bc));
    background:
        radial-gradient(circle at 12% 8%, color-mix(in srgb, oklch(var(--p)) 16%, transparent), transparent 28rem),
        radial-gradient(circle at 88% 4%, color-mix(in srgb, oklch(var(--a)) 12%, transparent), transparent 26rem),
        oklch(var(--b2));
    padding: 18px;
}

.playlist-hero,
.playlist-toolbar,
.playlist-status-strip,
.playlist-content {
    max-width: 1480px;
    margin-left: auto;
    margin-right: auto;
}

.playlist-hero {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(300px, 0.85fr);
    gap: 28px;
    align-items: stretch;
    min-height: 226px;
    margin-bottom: 14px;
    border: 1px solid var(--playlist-border);
    border-radius: var(--playlist-radius);
    background:
        radial-gradient(circle at 86% 18%, color-mix(in oklch, oklch(var(--p)) 18%, transparent), transparent 24rem),
        linear-gradient(
            105deg,
            oklch(var(--b1) / 0.98) 0%,
            oklch(var(--b1) / 0.96) 42%,
            color-mix(in oklch, oklch(var(--b2)) 88%, oklch(var(--p)) 6%) 68%,
            color-mix(in oklch, oklch(var(--b3)) 84%, oklch(var(--p)) 10%) 100%
        ),
        oklch(var(--b1));
    box-shadow: 0 20px 60px oklch(var(--bc) / 0.08);
    isolation: isolate;
    overflow: hidden;
}

.playlist-hero__copy {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: clamp(24px, 4vw, 44px);
}

.playlist-hero__label,
.playlist-modal__eyebrow {
    width: fit-content;
    margin-bottom: 14px;
    padding: 5px 10px;
    border-radius: 999px;
    background: oklch(var(--p) / 0.14);
    color: oklch(var(--p));
    font-size: 12px;
    font-weight: 700;
}

.playlist-hero h1 {
    max-width: 700px;
    margin: 0;
    font-size: clamp(30px, 4vw, 54px);
    line-height: 1.04;
    font-weight: 800;
    letter-spacing: 0;
}

.playlist-hero p {
    max-width: 55ch;
    margin-top: 16px;
    color: var(--playlist-muted);
    font-size: 15px;
    line-height: 1.75;
}

.playlist-hero__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 18px;
}

.playlist-hero__stage {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 226px;
    padding: 24px;
    background: transparent;
    isolation: isolate;
    overflow: hidden;
}

.playlist-hero__stage::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background:
        linear-gradient(
            90deg,
            transparent 0%,
            color-mix(in oklch, oklch(var(--b3)) 42%, transparent) 46%,
            color-mix(in oklch, oklch(var(--b3)) 68%, oklch(var(--p)) 8%) 100%
        );
    pointer-events: none;
}

.playlist-deck {
    position: relative;
    z-index: 1;
    width: min(450px, 100%);
    min-height: 216px;
}

.playlist-deck__item {
    position: absolute;
    left: calc(var(--playlist-index) * 7%);
    top: calc(var(--playlist-index) * 16px);
    display: grid;
    width: min(340px, 82vw);
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    padding: 12px;
    border: 1px solid oklch(var(--bc) / 0.13);
    border-radius: 16px;
    background:
        linear-gradient(135deg, oklch(var(--b1) / 0.9), oklch(var(--b2) / 0.88)),
        oklch(var(--b1));
    box-shadow: 0 14px 34px oklch(var(--bc) / 0.14);
}

.playlist-deck__item img {
    width: 64px;
    aspect-ratio: 1;
    border-radius: 12px;
    object-fit: cover;
}

.playlist-deck__item strong,
.playlist-deck__item span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.playlist-deck__item strong {
    font-size: 14px;
    font-weight: 800;
}

.playlist-deck__item span {
    margin-top: 5px;
    color: var(--playlist-muted);
    font-size: 12px;
}

.playlist-toolbar {
    position: sticky;
    top: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
    margin-bottom: 12px;
    padding: 10px;
    border: 1px solid var(--playlist-border);
    border-radius: var(--playlist-radius);
    background: color-mix(in oklch, oklch(var(--b1)) 92%, transparent);
    backdrop-filter: blur(14px);
    z-index: 3;
}

.playlist-toolbar__tabs {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 1px;
}

.playlist-toolbar__actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.playlist-tab,
.playlist-action-button {
    min-height: 38px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    transition: background-color 180ms ease, color 180ms ease, border-color 180ms ease, transform 180ms ease;
}

.playlist-tab {
    flex: 0 0 auto;
    padding: 0 14px;
    background: transparent;
    color: oklch(var(--bc) / 0.72);
}

.playlist-action-button {
    padding: 0 14px;
    background: oklch(var(--b2));
    color: oklch(var(--bc) / 0.76);
}

.playlist-action-button.is-primary,
.playlist-tab.is-active {
    background: oklch(var(--p));
    color: oklch(var(--pc));
    border-color: oklch(var(--p));
}

.playlist-tab:hover,
.playlist-action-button:hover {
    background: oklch(var(--b2));
    color: oklch(var(--bc));
}

.playlist-action-button.is-primary:hover,
.playlist-tab.is-active:hover {
    background: oklch(var(--p));
    color: oklch(var(--pc));
}

.playlist-tab:active,
.playlist-action-button:active,
.playlist-play-button:active,
.playlist-scroll-top:active,
.playlist-modal__icon-btn:active {
    transform: translateY(1px);
}

.playlist-status-strip {
    display: grid;
    grid-template-columns: 1.25fr 1fr 1fr 1fr;
    gap: 10px;
    margin-bottom: 12px;
}

.playlist-status-strip > div {
    min-width: 0;
    padding: 14px 16px;
    border: 1px solid var(--playlist-border);
    border-radius: 16px;
    background: var(--playlist-soft);
}

.playlist-status-strip__label {
    display: block;
    margin-bottom: 3px;
    color: var(--playlist-muted);
    font-size: 12px;
}

.playlist-status-strip strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 18px;
}

.playlist-content {
    padding: 2px 0 28px;
}

.playlist-card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    color: oklch(var(--bc) / 0.68);
    font-size: 13px;
}

.playlist-card-meta span {
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.playlist-play-button {
    display: inline-flex;
    width: 54px;
    height: 54px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    color: oklch(var(--p));
    filter: drop-shadow(0 12px 24px oklch(var(--bc) / 0.28));
    transition: color 180ms ease, transform 180ms ease;
}

.playlist-play-button:hover {
    color: oklch(var(--pc));
}

.playlist-loading,
.playlist-empty {
    display: flex;
    min-height: 260px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    border: 1px solid var(--playlist-border);
    border-radius: var(--playlist-radius);
    background: oklch(var(--b1) / 0.74);
    text-align: center;
}

.playlist-loading__grid {
    display: grid;
    width: min(500px, 78vw);
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.playlist-loading__grid span {
    aspect-ratio: 1.25;
    border-radius: 16px;
    background: linear-gradient(110deg, oklch(var(--b2)), oklch(var(--b3)), oklch(var(--b2)));
    background-size: 200% 100%;
}

.playlist-loading p,
.playlist-empty p {
    max-width: 38ch;
    color: var(--playlist-muted);
    font-size: 14px;
}

.playlist-empty__art {
    display: grid;
    width: 132px;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.playlist-empty__art span {
    aspect-ratio: 0.76;
    border: 1px solid oklch(var(--bc) / 0.12);
    border-radius: 14px;
    background: oklch(var(--b2));
}

.playlist-empty__art span:nth-child(2) {
    transform: translateY(-14px);
    background: oklch(var(--p) / 0.22);
}

.playlist-empty h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
}

.playlist-empty__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
}

.playlist-scroll-top {
    position: fixed;
    right: 24px;
    bottom: 24px;
    display: inline-flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: oklch(var(--p));
    color: oklch(var(--pc));
    box-shadow: 0 12px 30px oklch(var(--bc) / 0.2);
    transition: transform 180ms ease, background-color 180ms ease;
    z-index: 4;
}

.playlist-modal {
    color: oklch(var(--bc) / 0.92);
}

.playlist-modal::backdrop {
    background: color-mix(in oklch, oklch(var(--b1)) 18%, oklch(var(--bc) / 0.72));
    backdrop-filter: blur(10px);
}

.playlist-modal__box {
    position: relative;
    border-radius: 18px;
    border: 1px solid var(--playlist-border);
    background:
        radial-gradient(circle at 12% 0%, oklch(var(--p) / 0.12), transparent 32%),
        color-mix(in oklch, oklch(var(--b1)) 94%, transparent);
    box-shadow: 0 24px 58px oklch(var(--bc) / 0.18);
}

.playlist-modal__box--smart {
    display: flex;
    width: min(72rem, calc(100vw - 2rem));
    max-width: 72rem;
    max-height: min(82dvh, 48rem);
    flex-direction: column;
    padding: 1.2rem;
}

.playlist-modal__box--import {
    width: min(31rem, calc(100vw - 2rem));
    max-width: 31rem;
    padding: 1.35rem;
}

.playlist-modal__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 1rem;
}

.playlist-modal__title {
    margin-top: 0.18rem;
    font-size: 1.35rem;
    line-height: 1.15;
    font-weight: 800;
    color: oklch(var(--bc) / 0.96);
    letter-spacing: 0;
}

.playlist-modal__hint {
    margin: 0.65rem 0 1rem;
    color: oklch(var(--bc) / 0.62);
    font-size: 0.92rem;
    line-height: 1.6;
}

.playlist-modal__icon-btn {
    display: inline-flex;
    width: 2.45rem;
    height: 2.45rem;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    background: oklch(var(--b2) / 0.84);
    color: oklch(var(--bc) / 0.92);
    font-weight: 800;
    box-shadow: 0 12px 28px oklch(var(--bc) / 0.08);
    transition: transform 180ms ease, background-color 180ms ease;
}

.playlist-modal__icon-btn--floating {
    position: absolute;
    top: 1rem;
    right: 1rem;
}

.playlist-modal__icon-btn:disabled {
    opacity: 0.45;
}

.playlist-modal__body {
    overflow-y: auto;
    padding-right: 0.2rem;
}

.playlist-modal__body--smart {
    flex: 1;
}

.playlist-modal__field {
    margin-bottom: 1rem;
}

.playlist-modal__label {
    display: block;
    margin-bottom: 0.42rem;
    color: oklch(var(--bc) / 0.78);
    font-size: 0.82rem;
    font-weight: 800;
}

.playlist-modal__input,
.playlist-modal__file {
    width: 100%;
    max-width: none;
    border-radius: 1rem;
    border-color: oklch(var(--bc) / 0.16);
    background-color: oklch(var(--b1) / 0.88);
    color: oklch(var(--bc) / 0.92);
}

.playlist-modal__input:focus,
.playlist-modal__file:focus {
    outline: 2px solid oklch(var(--bc) / 0.22);
    outline-offset: 2px;
}

.playlist-modal__rules {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.playlist-modal__rule-group {
    padding: 0.85rem;
    border-radius: 16px;
    background: color-mix(in oklch, oklch(var(--b2)) 72%, transparent);
    box-shadow: 0 12px 28px oklch(var(--bc) / 0.06);
}

.playlist-modal__rule-head,
.playlist-modal__condition-row,
.playlist-modal__limit-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
}

.playlist-modal__rule-head {
    justify-content: space-between;
    margin-bottom: 0.65rem;
}

.playlist-modal__rule-title {
    color: oklch(var(--bc) / 0.94);
    font-size: 0.9rem;
    font-weight: 800;
}

.playlist-modal__rule-actions {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.35rem;
}

.playlist-modal__rule-actions .btn {
    width: 2rem;
    height: 2rem;
    min-height: 2rem;
    border-radius: 999px;
    border-color: oklch(var(--bc) / 0.18);
    background: oklch(var(--b1) / 0.76);
    color: oklch(var(--bc) / 0.9);
}

.playlist-modal__condition-row {
    margin-top: 0.5rem;
    padding-left: 1rem;
}

.playlist-modal__logic-chip {
    display: inline-flex;
    min-height: 2rem;
    align-items: center;
    justify-content: center;
    padding: 0 0.7rem;
    border-radius: 999px;
    background: oklch(var(--n));
    color: oklch(var(--nc));
    font-size: 0.78rem;
    font-weight: 800;
}

.playlist-modal__logic-chip--and {
    background: oklch(var(--bc) / 0.1);
    color: oklch(var(--bc) / 0.76);
}

.playlist-modal__select {
    min-width: 10rem;
}

.playlist-modal__select--operator {
    min-width: 7.5rem;
}

.playlist-modal__value-field {
    display: flex;
    min-width: 13rem;
    flex: 1;
    align-items: center;
}

.playlist-modal__unit {
    margin-left: 0.5rem;
    color: oklch(var(--bc) / 0.58);
    font-size: 0.82rem;
    font-weight: 700;
    white-space: nowrap;
}

.playlist-modal__divider {
    margin: 0.8rem 0 0.1rem;
    color: oklch(var(--bc) / 0.46);
    font-size: 0.72rem;
    font-weight: 800;
    text-align: center;
    letter-spacing: 0;
}

.playlist-modal__limit-row {
    margin-top: 0.95rem;
    padding: 0.85rem;
    border-radius: 16px;
    background: oklch(var(--b2) / 0.58);
}

.playlist-modal__number {
    width: 5.2rem;
}

.playlist-modal__unit-select {
    width: 4.8rem;
    border-radius: 1rem;
    background-color: oklch(var(--b1) / 0.88);
    color: oklch(var(--bc) / 0.92);
}

.playlist-modal__toggle {
    width: fit-content;
    min-width: 9rem;
    margin-top: 0.7rem;
    border-radius: 999px;
    background: oklch(var(--b2) / 0.68);
    box-shadow: 0 10px 24px oklch(var(--bc) / 0.06);
}

.playlist-modal__toggle--loose {
    width: 100%;
    margin-top: 1rem;
}

.playlist-modal__toggle .label {
    gap: 0.75rem;
    padding: 0.65rem 0.85rem;
}

.playlist-modal__toggle .label-text {
    color: oklch(var(--bc) / 0.78);
    font-weight: 800;
}

.playlist-modal__help {
    color: oklch(var(--bc) / 0.58);
}

.playlist-modal__actionbar {
    margin-top: 1rem;
    padding-top: 0.85rem;
    border-top: 1px solid oklch(var(--bc) / 0.12);
}

.playlist-modal__actionbar .btn {
    min-height: 2.35rem;
    padding-left: 1rem;
    padding-right: 1rem;
    border-radius: 999px;
    font-weight: 800;
}

.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

@media (prefers-reduced-motion: no-preference) {
    .playlist-hero,
    .playlist-toolbar,
    .playlist-status-strip,
    .playlist-content {
        animation: playlistFadeIn 360ms ease both;
    }

    .playlist-tile-shell.is-animated {
        animation: playlistTileIn 420ms ease both;
        animation-delay: calc(var(--tile-index) * 42ms);
    }

    .playlist-loading__grid span {
        animation: playlistSkeleton 1.2s ease-in-out infinite;
    }
}

@keyframes playlistFadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes playlistTileIn {
    from {
        opacity: 0;
        transform: translateY(14px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes playlistSkeleton {
    0% {
        background-position: 100% 0;
    }
    100% {
        background-position: -100% 0;
    }
}

@media screen and (max-width: 900px) {
    .playlist-library {
        padding: 12px;
    }

    .playlist-hero {
        grid-template-columns: 1fr;
    }

    .playlist-hero__stage {
        min-height: 210px;
    }

    .playlist-toolbar {
        grid-template-columns: 1fr;
    }

    .playlist-toolbar__actions {
        justify-content: flex-end;
    }
}

@media screen and (max-width: 768px) {
    .playlist-modal {
        align-items: flex-end;
    }

    .playlist-modal__box,
    .playlist-modal__box--smart,
    .playlist-modal__box--import {
        width: 100%;
        max-width: 100%;
        max-height: 86dvh;
        margin: 0;
        padding: 1rem;
        border-radius: 18px 18px 0 0;
    }

    .playlist-modal__head {
        padding-bottom: 0.8rem;
    }

    .playlist-modal__title {
        font-size: 1.2rem;
    }

    .playlist-modal__condition-row,
    .playlist-modal__limit-row {
        align-items: stretch;
        flex-wrap: wrap;
        padding-left: 0;
    }

    .playlist-modal__select,
    .playlist-modal__select--operator,
    .playlist-modal__value-field {
        min-width: min(100%, 10rem);
        flex: 1 1 10rem;
    }

    .playlist-modal__rule-group,
    .playlist-modal__limit-row {
        padding: 0.75rem;
    }

    .playlist-modal__actionbar {
        position: sticky;
        bottom: -1rem;
        margin-right: -1rem;
        margin-bottom: -1rem;
        margin-left: -1rem;
        padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
        background: color-mix(in oklch, oklch(var(--b1)) 94%, transparent);
        backdrop-filter: blur(12px);
    }
}

@media screen and (max-width: 640px) {
    .playlist-hero__copy {
        padding: 22px;
    }

    .playlist-hero h1 {
        font-size: 30px;
    }

    .playlist-deck {
        width: 100%;
        min-height: 214px;
    }

    .playlist-deck__item {
        left: calc(var(--playlist-index) * 4%);
        width: 88%;
    }

    .playlist-status-strip {
        grid-template-columns: 1fr;
    }

    .playlist-loading__grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .playlist-scroll-top {
        right: 16px;
        bottom: 16px;
    }
}
</style>
